import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal, Popup as DsmPopup } from '@kcftech/design-system';
import { ConfiguredAppProvider } from 'contexts/ConfiguredAppProvider';
import { MediaSize, useIsViewportAtMost } from 'custom_hooks/useWindowSize';
import { CSSProperties, forwardRef, ReactNode, RefObject } from 'react';
import styled from 'styled-components';
import { backgroundColor, menuBoxShadow } from 'styles/theme';
import { kendoPopupZIndex } from 'styles/variables';

const PopupContentFullScreenHeader = styled.div`
    flex-grow: 1;
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-rows: auto;
    align-items: center;
`;

const PopupContent = styled.div`
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    box-shadow: ${menuBoxShadow};
    background: ${backgroundColor};
`;

const ConfiguredAppProviderContainer = styled(ConfiguredAppProvider)`
    flex-grow: 1;
    display: flex;
    flex-direction: column;
`;

export type PopupAnchorRef = RefObject<HTMLElement | SVGElement>;

export type PopupProps = Readonly<{
    anchorRef: PopupAnchorRef;
    children?: ReactNode;
    fullScreenHeader?: ReactNode;
    ariaLabel?: string;
    popupStyle?: CSSProperties;
    focusLock?: boolean;
    isFullScreen?: boolean;
    onClose?: () => void;
    'test-id': string;
}>;

const Popup = forwardRef<HTMLDivElement, PopupProps>(
    (
        {
            anchorRef,
            fullScreenHeader,
            children,
            ariaLabel = 'popup',
            popupStyle,
            focusLock = false,
            isFullScreen = false,
            onClose,
            'test-id': testId,
        },
        ref
    ) => {
        const renderFullScreen = useIsViewportAtMost(MediaSize.xsmall) || isFullScreen;

        return renderFullScreen ? (
            <Modal
                data-testid={testId}
                ref={ref}
                windowClassName="kcf-popup"
                title={
                    <PopupContentFullScreenHeader>
                        <div>{fullScreenHeader}</div>
                        {onClose && (
                            <div>
                                <Button
                                    type="button"
                                    className="flex-row align-center"
                                    aria-label="Close"
                                    onClick={onClose}
                                    appearance="subtle"
                                    spacing="none"
                                >
                                    <FontAwesomeIcon
                                        className="pl-10 pt-2"
                                        icon={faTimes}
                                    />
                                </Button>
                            </div>
                        )}
                    </PopupContentFullScreenHeader>
                }
                contentNoPadding
            >
                {children}
            </Modal>
        ) : (
            <DsmPopup
                data-testid={testId}
                ref={ref}
                targetRef={anchorRef}
                aria-label={ariaLabel}
                focusLock={focusLock}
                className="kcf-popup"
                position="right"
                style={{
                    zIndex: kendoPopupZIndex,
                    display: 'flex',
                    flexDirection: 'column',
                    ...popupStyle,
                }}
            >
                <ConfiguredAppProviderContainer>
                    <PopupContent style={{ borderRadius: 5, ...popupStyle }}>
                        {children}
                    </PopupContent>
                </ConfiguredAppProviderContainer>
            </DsmPopup>
        );
    }
);

export { Popup };
